"use client";
import React, { useState } from "react";
import axios from "axios";
import * as Yup from "yup";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";
import FileUploader from "./FileUploader";
import { useRouter } from "next/navigation";
import { PHONE_REGEX } from "@/utils/common";
const FormInput = dynamic(() => import("./FormInput"));
import { Formik, Form, Field, ErrorMessage } from "formik";
import { IMAGE_TYPE } from "@/utils/constants";
const Button = dynamic(() => import("@/Components/Button"));

const initialValues = {
  first_name: "",
  last_name: "",
  work_email: "",
  contact_number: "",
  designation: "",
  company_name: "",
  company_website: "",
  company_linkedin_page: "",
  location: "",
  services: "",
  technologies_supporting: "",
  total_employees: "",
  bench_strength: "",
  partnership_reason: "",
  similar_partnerships: "",
};

const VendorFormStepperForm: React.FC = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const API_BASE_URL = process.env.NEXT_PUBLIC_PYTHON_APP_API_BASE_URL;
  const TOKEN_VENDOR = process.env.NEXT_PUBLIC_TOKEN_VENDOR;
  const validationSchema = Yup.object({
    first_name: Yup.string().trim().required().label("Full name"),
    last_name: Yup.string().trim().required().label("Last name"),
    work_email: Yup.string()
      .trim()
      .email("Email is not valid.")
      .max(100, "Email is too long")
      .required()
      .label("Email")
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
    contact_number: Yup.string()
      .nullable()
      .required()
      .label("Contact Number")
      .matches(PHONE_REGEX, "Invalid Contact Number"),
    company_name: Yup.string().required().label("Company name"),
    company_website: Yup.string().required().url().label("Company Website"),
    company_linkedin_page: Yup.string().url().required().label("Linkedin URL"),
    designation: Yup.string().required().label("Designation"),
    location: Yup.string().required().label("Location"),
    services: Yup.string().required().label("Services"),
    technologies_supporting: Yup.string()
      .required()
      .label("Technologies Supporting"),
    total_employees: Yup.string().required().label("Number of Employees"),
    bench_strength: Yup.string().required().label("Bench Strength"),
    partnership_reason: Yup.string().required().label("Partnership Reason"),
    similar_partnerships: Yup.string()
      .required()
      .label("Similar Partnership Description"),
    file: Yup.mixed()
      .nullable()
      .test(
        "type",
        "Only the Image and Document formats are accepted: .jpeg, .jpg, .png, .bmp, .pdf, .doc, .docx and .odt",
        () => {
          let flag = true;
          if (selectedFiles && selectedFiles?.length > 0) {
            flag = false;
            for (let i = 0; i < selectedFiles?.length; i++) {
              if (IMAGE_TYPE.includes(selectedFiles[i]?.type)) flag = true;
              else {
                flag = false;
                break;
              }
            }
          }
          return flag;
        }
      ),
  });

  const ErrorMessageComponent = (props: any) => (
    <div className="text-red-500 font-medium text-xs mt-2" role="alert">
      {props.children}
    </div>
  );

  const handleSubmit = async (
    values: any,
    { setSubmitting }: any
  ) => {
    try {
      setIsLoading(true);
      const payload = { ...values } as Record<string, any>;
      const form_data = new FormData();
      for (const key in payload) {
        if (key !== "file" && payload[key])
          form_data.append(key, payload[key]?.toString());
        else if (key === "file") {
          selectedFiles.forEach((file) => {
            form_data.append("rate_card", file);
          });
        }
      }
      const config = {
        headers: {
          TOKEN: `${TOKEN_VENDOR ?? ""}`,
          "Content-Type": "application/multipart/form-data",
        },
      };
      const res = await axios.post(`${API_BASE_URL}`, form_data, config);
      if (res?.data?.id) {
        setSubmitting(false);
        toast.success(res?.data?.message ?? "Your request has been sent.");
        router.push("/thank-you");
      } else toast.error(res?.data?.message ?? "Something went wrong");
    } catch (error) {
      console.log("handleSubmit ~ error:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 4000);
    }
  };

  return (
    <>
      <div
        className={`relative z-10 pb-[56px] lg:pb-[80px] xl:pb-[100px] xl:mt-[-200px] lg:mt-[-150px] mt-[-100px]`}
      >
        <div className="container">
          <div
            className={`bg-[url(/images/Quote-bg.webp)] bg-no-repeat bg-cover bg-center h-full w-full`}
          >
            <Formik
              onSubmit={handleSubmit}
              initialValues={initialValues}
              validationSchema={validationSchema}
            >
              {({ setFieldValue }) => (
                <Form className="p-[24px_24px] lg:p-[48px_24px] xl:p-[48px_48px] h-full items-stretch flex flex-col justify-between">
                  <div className="flex flex-col h-full justify-between min-h-[500px]">
                    <div className="xl:grid xl:grid-cols-3 lg:grid-cols-1 grid-cols-1 lg:gap-4 gap-3 flex flex-col">
                      <div className="flex lg:flex-row flex-col gap-4 col-span-3">
                        <FormInput
                          key="First Name"
                          name="first_name"
                          placeholder="First Name"
                          required
                        />
                        <FormInput
                          key="Last Name"
                          name="last_name"
                          placeholder="Last Name"
                          required
                        />
                      </div>

                      <FormInput
                        key="Work Email"
                        name="work_email"
                        placeholder="Work Email"
                        type="email"
                        required
                      />
                      <FormInput
                        key="Contact Number"
                        name="contact_number"
                        placeholder="Contact Number"
                        type="tel"
                        required
                      />
                      <FormInput
                        key="Designation"
                        name="designation"
                        placeholder="Designation"
                        required
                      />
                      <div className="col-span-3">
                        <FormInput
                          key="Company Name"
                          name="company_name"
                          placeholder="Company Name"
                          required
                        />
                      </div>
                      <div className="flex lg:flex-row flex-col gap-4 col-span-3">
                        <FormInput
                          key="Company Website"
                          name="company_website"
                          placeholder="Company Website"
                          required
                        />
                        <FormInput
                          key="Company Linkedin URL"
                          name="company_linkedin_page"
                          placeholder="Company Linkedin URL"
                          required
                        />
                      </div>
                      <div className="flex lg:flex-row flex-col gap-4 col-span-3">
                        <FormInput
                          key="Location"
                          name="location"
                          placeholder="Location"
                          required
                        />
                        <FormInput
                          type="number"
                          key="Total number of employees"
                          name="total_employees"
                          placeholder="Total number of employees"
                          required
                        />
                      </div>

                      <div className="flex lg:flex-row flex-col gap-4 col-span-3">
                        <div className="w-full">
                          <div className="relative text-[#040405] bg-white border border-[#b0d9f7] outline-none w-full h-auto  font-bold transition ease-out duration-200 block">
                            <div className="flex justify-between items-center flex-wrap gap-[10px] border-b border-[#B0D9F7] ">
                              <label
                                htmlFor="contact_on"
                                className="text-[12px] font-regular text-[#32343B] p-[16px] "
                              >
                                Services*
                              </label>
                            </div>
                            <Field
                              as="textarea"
                              name="services"
                              id="services"
                              className="border-0 outline-0 w-full px-4 py-5 text-subtitle-4 text-primary-text font-bold"
                              rows={4}
                            />
                          </div>
                          <ErrorMessage
                            name="services"
                            component={ErrorMessageComponent}
                            id="services-error"
                          />
                        </div>
                        <div className="w-full">
                          <div className="relative text-[#040405] bg-white border border-[#b0d9f7] outline-none w-full h-auto  font-bold transition ease-out duration-200 block">
                            <div className="flex justify-between items-center flex-wrap gap-[10px] border-b border-[#B0D9F7] ">
                              <label
                                htmlFor="contact_on"
                                className="text-[12px] font-regular text-[#32343B] p-[16px]"
                              >
                                Technologies supporting at present*
                              </label>
                            </div>
                            <Field
                              as="textarea"
                              name="technologies_supporting"
                              id="technologies_supporting"
                              className="border-0 outline-0 w-full px-4 py-5 text-subtitle-4 font-bold text-primary-text"
                              rows={4}
                            />
                          </div>
                          <ErrorMessage
                            name="technologies_supporting"
                            component={ErrorMessageComponent}
                            id="technologies_supporting-error"
                          />
                        </div>
                      </div>

                      <div className="flex lg:flex-row flex-col gap-4 col-span-3">
                        <div className="w-full">
                          <div className="relative text-[#040405] bg-white border border-[#b0d9f7] outline-none w-full h-auto  font-bold transition ease-out duration-200 block">
                            <div className="flex justify-between items-center flex-wrap gap-[10px] border-b border-[#B0D9F7] xl:min-h-[80px]  min-h-[100px] ">
                              <label
                                htmlFor="contact_on"
                                className="text-[12px] font-regular text-[#32343B] p-[16px] "
                              >
                                Describe your bench strength*
                                <span className="block text-[#83858C] font-regular text-[12px]">
                                  (Information regarding the available resources
                                  you have in-house)
                                </span>
                              </label>
                            </div>
                            <Field
                              rows={4}
                              as="textarea"
                              name="bench_strength"
                              id="bench_strength"
                              className="border-0 outline-0 w-full px-4 py-5 text-subtitle-4 font-bold text-primary-text"
                              placeholder=""
                            />
                          </div>
                          <ErrorMessage
                            name="bench_strength"
                            component={ErrorMessageComponent}
                            id="bench_strength-error"
                          />
                        </div>
                        <div className="w-full">
                          <div className="relative text-[#040405] bg-white border border-[#b0d9f7] outline-none w-full h-auto  font-bold transition ease-out duration-200 block">
                            <div className="flex justify-between items-center flex-wrap gap-[10px] border-b border-[#B0D9F7]  xl:min-h-[80px]  min-h-[100px] ">
                              <label
                                htmlFor="contact_on"
                                className="text-[12px] font-regular text-[#32343B] p-[16px]"
                              >
                                Why should we partner with you?*
                                <span className="block text-[#83858C] font-regular text-[12px]">
                                  {`(Please be specific by studying our service
                                verticals and make sure your answer is
                                meaningful for our partnership, it's important)`}
                                </span>
                              </label>
                            </div>
                            <Field
                              as="textarea"
                              name="partnership_reason"
                              id="partnership_reason"
                              placeholder=""
                              className="border-0 outline-0 w-full px-4 py-5 text-subtitle-4 font-bold text-primary-text"
                              rows={4}
                            />
                          </div>
                          <ErrorMessage
                            name="partnership_reason"
                            component={ErrorMessageComponent}
                            id="partnership_reason-error"
                          />
                        </div>
                      </div>
                      <div className="flex lg:flex-row flex-col gap-4 col-span-3">
                        <div className="w-full">
                          <div className="relative text-[#040405] bg-white border border-[#b0d9f7] outline-none w-full h-auto  font-bold transition ease-out duration-200 block">
                            <div className="flex justify-between items-center flex-wrap gap-[10px] border-b border-[#B0D9F7] min-h-[90px]">
                              <label
                                htmlFor="contact_on"
                                className="text-[12px] font-regular text-[#32343B] p-[16px] "
                              >
                                Describe similar partnerships you have*
                                <span className="block text-[#83858C] font-regular text-[12px]">
                                  (Please be real about the information as later
                                  we would like to speak with the same companies
                                  as your references if we move forward)
                                </span>
                              </label>
                            </div>
                            <Field
                              as="textarea"
                              name="similar_partnerships"
                              id="similar_partnerships"
                              placeholder=""
                              className="border-0 outline-0 w-full px-4 py-5 text-subtitle-4 font-bold text-primary-text"
                              rows={4}
                            />
                          </div>
                          <ErrorMessage
                            name="similar_partnerships"
                            component={ErrorMessageComponent}
                            id="similar_partnerships-error"
                          />
                        </div>
                        <div className="w-full">
                          <div className="flex flex-col h-full">
                            <FileUploader
                              file={file}
                              onFileChange={(val) => {
                                setFile(val);
                                setFieldValue("file", val);
                                setSelectedFiles([val]);
                              }}
                            />
                          </div>
                            <ErrorMessage
                              name="file"
                              component={ErrorMessageComponent}
                              id="file-error"
                            />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-[24px] text-center flex justify-end items-center gap-[10px]">
                    <Button
                      variant="small"
                      className="my-0"
                      label="Submit"
                      type="submit"
                      loading={isLoading}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorFormStepperForm;
